import { useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  writeBatch,
  increment,
  addDoc,
  limit as qLimit,
} from "firebase/firestore";

/* ───────────────────────── constants / helpers ───────────────────────── */
const LEVEL_RATES = [0.4, 0.2, 0.1]; // Buyer commission split L1/L2/L3
const MIRROR_REFERRER_NOTIF_TO_USERS = true;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const clamp0 = (n) => Math.max(0, round2(n));

// UI currency is always LKR (preview-only)
const UI_CURRENCY = "LKR";
function fmt(_curIgnored, n) {
  return `${UI_CURRENCY} ${Number(n || 0).toLocaleString()}`;
}
function makeNotif({
  title,
  body,
  type = "info",
  link = "",
  cta = "",
  extra = {},
}) {
  return {
    title,
    body,
    message: body,
    text: body,
    description: body,
    type,
    link,
    cta,
    read: false,
    seen: false,
    createdAt: serverTimestamp(),
    ...extra,
  };
}
function setAllNotifTargets(batch, uid, payload, opts = {}) {
  const { toUsers = true, toReferralUsers = false } = opts;
  batch.set(doc(collection(db, "notifications")), { userId: uid, ...payload });
  if (toUsers)
    batch.set(doc(collection(db, "users", uid, "notifications")), payload);
  if (toReferralUsers)
    batch.set(
      doc(collection(db, "referral_users", uid, "notifications")),
      payload,
    );
}

/* ───────────────────────── lookups ───────────────────────── */
async function findReferralUserByUid(uid) {
  if (!uid) return null;
  const qs = await getDocs(
    query(collection(db, "referral_users"), where("uid", "==", uid), qLimit(1)),
  );
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}
async function findReferralUserByCode(code) {
  if (!code) return null;
  const qs = await getDocs(
    query(
      collection(db, "referral_users"),
      where("referralCode", "==", code),
      qLimit(1),
    ),
  );
  if (qs.empty) return null;
  const d = qs.docs[0];
  return { id: d.id, ...d.data() };
}
async function findReferralCodeMapping(code) {
  if (!code) return null;
  const asId = await getDoc(doc(db, "referral_codes", code));
  if (asId.exists()) return { id: asId.id, ...asId.data() };
  const qs = await getDocs(
    query(
      collection(db, "referral_codes"),
      where("code", "==", code),
      qLimit(1),
    ),
  );
  if (!qs.empty) {
    const d = qs.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}
async function findUserByCode(code) {
  if (!code) return null;
  const candidates = ["referralCode", "sponsorCode", "referredBy"];
  for (const field of candidates) {
    const qs = await getDocs(
      query(collection(db, "users"), where(field, "==", code), qLimit(1)),
    );
    if (!qs.empty) {
      const d = qs.docs[0];
      return { id: d.id, ...d.data() };
    }
  }
  return null;
}
async function getUidForReferralCode(codeRaw) {
  const code = (codeRaw || "").trim();
  if (!code) return null;
  const ru = await findReferralUserByCode(code);
  if (ru?.uid) return ru.uid;
  const rc = await findReferralCodeMapping(code);
  const rcUid = rc?.ownerUserId || rc?.userId || rc?.uid || null;
  if (rcUid) return rcUid;
  const u = await findUserByCode(code);
  if (u?.id) return u.id;
  return null;
}
async function getSponsor(uid) {
  try {
    if (!uid) return null;

    const childRU = await findReferralUserByUid(uid);
    if (childRU) {
      const sponsorCode = (
        childRU.referredBy ||
        childRU.sponsorCode ||
        childRU.parentCode ||
        ""
      ).trim();
      if (sponsorCode) {
        const sponsorUid = await getUidForReferralCode(sponsorCode);
        if (sponsorUid && sponsorUid !== uid) return sponsorUid;
      }
    }

    const us = await getDoc(doc(db, "users", uid));
    if (us.exists()) {
      const u = us.data() || {};
      const sponsorUid =
        u.sponsorId || u.referrerUserId || u.referredByUid || null;
      if (sponsorUid && sponsorUid !== uid) return sponsorUid;

      const sponsorCode2 = (
        u.referredBy ||
        u.sponsorCode ||
        u.referralCode ||
        ""
      ).trim();
      if (sponsorCode2) {
        const sponsorUid2 = await getUidForReferralCode(sponsorCode2);
        if (sponsorUid2 && sponsorUid2 !== uid) return sponsorUid2;
      }
    }
    return null;
  } catch (e) {
    console.error("[getSponsor] failed for uid:", uid, e);
    return null;
  }
}

async function getUser(uid) {
  if (!uid) return null;
  const s = await getDoc(doc(db, "users", uid));
  return s.exists() ? { id: uid, ...s.data() } : null;
}

/**
 * Split rules:
 * - price (from course doc) is the REAL instructor payout (net). We show and pay this 1:1.
 * - Buyer commission distribution uses the stored `commission` pool.
 */
async function getCourseSplit(courseId, orderAmount, order = {}) {
  const snap = await getDoc(doc(db, "courses", courseId));
  if (!snap.exists()) {
    const endPrice = Number(orderAmount || 0);
    return {
      endPrice,
      commission: 0,
      company: 0,
      service: 0,
      others: 0,
      instructor: 0, // no price in doc → treat as 0
      instructorUid: null,
      source: "fallback",
      courseDoc: {},
    };
  }

  const c = snap.data() || {};

  // End price shown to the buyer should match the entered course price
  const endPrice = Number(c.price ?? c.finalPrice ?? orderAmount ?? 0) || 0;

  const commission = clamp0(
    Number(
      order?.affiliateTotalCommissionAmount ??
        order?.affiliateCommissionAmount ??
        c.affiliateTotalCommissionAmount ??
        c.affiliateCommissionAmount ??
        c.commission ??
        0,
    ),
  );
  const company = clamp0(Number(c.company || 0));
  const service = clamp0(Number(c.service || 0));
  const others = clamp0(Number(c.others || 0));

  const instructorNet = clamp0(
    endPrice - (commission + company + service + others),
  );

  const instructorUid =
    c.instructorUserId ||
    c.instructorUid ||
    c.instructorId ||
    c.createdBy ||
    c.ownerUid ||
    c.ownerId ||
    c.userId ||
    null;

  return {
    endPrice,
    commission,
    company,
    service,
    others,
    instructor: instructorNet,
    instructorUid,
    source: "entered_price_less_commission",
    courseDoc: c,
  };
}

function buildInvoiceHtml({ order, split, dist }) {
  const currency = order.currency || "LKR";
  const row = (label, value) =>
    `<tr><td style="padding:6px 0;color:#666">${label}</td><td style="padding:6px 0;text-align:right;font-weight:600">${currency} ${Number(
      value || 0,
    ).toLocaleString()}</td></tr>`;
  const commissionRows = dist
    .map(
      (d) =>
        `<tr><td style="padding:4px 0;color:#666">Referrer L${
          d.level
        }</td><td style="padding:4px 0;text-align:right">${currency} ${Number(
          d.amount || 0,
        ).toLocaleString()}</td></tr>`,
    )
    .join("");
  return `
    <div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;max-width:560px;margin:auto">
      <h2 style="margin:0 0 6px">Payment Approved</h2>
      <div style="color:#666;margin-bottom:12px">Order <b>${
        order.id
      }</b> • ${new Date().toLocaleString()}</div>
      <div style="padding:12px;border:1px solid #eee;border-radius:12px">
        <div style="font-weight:700;margin-bottom:6px">${
          order.courseTitle || "Course"
        }</div>
        <table style="width:100%;border-collapse:collapse">
          ${row("Paid Amount", order.amount)}
          ${row("End Price", split.endPrice)}
        </table>
        <div style="margin-top:10px;font-weight:700">Split</div>
        <table style="width:100%;border-collapse:collapse">
          ${row("Instructor (net)", split.instructor)}
          ${row("Company", split.company)}
          ${row("Service", split.service)}
          ${row("Others", split.others)}
          ${row("Commission (pool)", split.commission)}
        </table>
        ${
          commissionRows
            ? `<div style="margin-top:10px;font-weight:700">Commission Distribution</div>
               <table style="width:100%;border-collapse:collapse">${commissionRows}</table>`
            : ""
        }
      </div>
    </div>`;
}

/* ───────────────────────── people resolver (names & phones) ───────────────────────── */
const personMemo = new Map();
async function getPerson(uid) {
  if (!uid) return null;
  if (personMemo.has(uid)) return personMemo.get(uid);
  const u = await getUser(uid);
  const cp = u?.checkoutProfile || {};
  const name =
    cp?.name ||
    u?.fullName ||
    u?.displayName ||
    u?.name ||
    u?.profile?.name ||
    uid;
  const phone =
    cp?.phone ||
    u?.phone ||
    u?.whatsapp ||
    u?.profile?.phone ||
    u?.contactPhone ||
    "";
  const res = { uid, name, phone };
  personMemo.set(uid, res);
  return res;
}
async function resolvePeople(uids = []) {
  const uniq = [...new Set(uids.filter(Boolean))];
  const out = {};
  await Promise.all(
    uniq.map(async (id) => {
      out[id] = await getPerson(id);
    }),
  );
  return out;
}

/* ───────────────────────── component ───────────────────────── */

export default function OrdersReview() {
  const [rows, setRows] = useState([]);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalKind] = useState(null); // 'approve' | 'decline'
  const [currentRow] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);

  // previews
  const [splitPreview] = useState(null);
  const [upliftPreview] = useState({
    hasReferredInstructor: false,
    upliftTotal: 0,
    chain: [],
    dist: [],
    pool: 0,
    upliftPctUsed: 0,
  });
  const [buyerPreview] = useState({
    chain: [null, null, null],
    dist: [],
    pool: 0,
    base: 0,
  });
  const [people, setPeople] = useState({});

  const modalRef = useRef(null);

  // Resolve buyer + referrer display names for the list
  useEffect(() => {
    if (!rows?.length) return;
    const uids = new Set();
    rows.forEach((r) => {
      if (r.userId) uids.add(r.userId);
      if (r.referrerUserId) uids.add(r.referrerUserId);
      if (r.refAttribution?.refUid) uids.add(String(r.refAttribution.refUid));
    });
    (async () => {
      const resolved = await resolvePeople([...uids]);
      // merge into existing people so openApprove keeps its extras
      setPeople((prev) => ({ ...prev, ...resolved }));
    })();
  }, [rows]);

  useEffect(() => {
    const qy = query(
      collection(db, "orders"),
      where("status", "in", ["submitted"]),
    );
    const unsub = onSnapshot(qy, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // close on ESC / outside
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setModalOpen(false);
    }
    function onClick(e) {
      if (
        modalOpen &&
        modalRef.current &&
        !modalRef.current.contains(e.target)
      ) {
        setModalOpen(false);
      }
    }
    if (modalOpen) {
      window.addEventListener("keydown", onKey);
      window.addEventListener("mousedown", onClick);
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [modalOpen]);

  /* ───────────────────────── approve logic ───────────────────────── */
  const approve = async (row) => {
    try {
      setConfirmBusy(true);
      if (!row?.id || !row?.userId || !row?.courseId) {
        alert("Missing required order fields.");
        setConfirmBusy(false);
        return;
      }

      const buyer = await getUser(row.userId);
      const split = await getCourseSplit(row.courseId, row.amount, row);
      const {
        endPrice,
        commission,
        company,
        service,
        others,
        instructor: instructorBase, // this is course.price
        instructorUid,
      } = split;

      // Buyer-side commission distribution
      let L1 =
        (row.referrerUserId || row.refAttribution?.refUid || "").trim() || null;
      if (!L1) {
        const codeRaw = (
          row.referralCode ||
          row.refAttribution?.code ||
          ""
        ).trim();
        if (codeRaw) L1 = await getUidForReferralCode(codeRaw);
      }
      if (!L1) {
        const fallback = await getSponsor(row.userId);
        if (fallback) L1 = fallback;
      }
      const L2 = await getSponsor(L1);
      const L3 = await getSponsor(L2);
      const chain = [L1, L2, L3];

      const commissionBase = clamp0(commission);
      const shares = LEVEL_RATES.map((r) => round2(commissionBase * r));
      let commissionPoolAmount = 0;
      const dist = [];
      chain.forEach((uid, i) => {
        const amount = shares[i];
        if (uid) dist.push({ userId: uid, level: i + 1, amount });
        else commissionPoolAmount += amount;
      });
      const allocated = round2(dist.reduce((t, d) => t + d.amount, 0));
      const remainder = round2(
        commissionBase - allocated - round2(commissionPoolAmount),
      );
      commissionPoolAmount = round2(commissionPoolAmount + remainder);

      // writes
      const batch = writeBatch(db);
      batch.update(doc(db, "orders", row.id), {
        status: "paid",
        approvedAt: serverTimestamp(),
        approvedBy: "admin",
        endPrice,
        commissionAmount: commissionBase,
        commissionDistribution: dist,
        commissionPoolAmount,
        instructorAmount: instructorBase,
        companyAmount: company,
        serviceAmount: service,
        othersAmount: others,
        instructorUserId: instructorUid || null,
        uplineChain: [
          { level: 1, uid: L1 || null },
          { level: 2, uid: L2 || null },
          { level: 3, uid: L3 || null },
        ],
        instructorReferralUpliftTotal: 0,
        instructorReferralDistribution: [],
        instructorReferralPoolAmount: 0,
        instructorUplineChain: [],
      });

      // enroll
      batch.set(
        doc(db, "enrollments", `${row.userId}_${row.courseId}`),
        {
          userId: row.userId,
          courseId: row.courseId,
          enrolledAt: serverTimestamp(),
          source: "bank_manual",
          orderId: row.id,
        },
        { merge: true },
      );

      // payouts + wallets + notifications (buyer side)
      for (const d of dist) {
        const payoutRef = doc(collection(db, "payouts"));
        batch.set(payoutRef, {
          userId: d.userId,
          orderId: row.id,
          courseId: row.courseId,
          courseTitle: row.courseTitle || "",
          amount: d.amount,
          level: d.level,
          currency: row.currency || "LKR",
          status: "pending",
          createdAt: serverTimestamp(),
          source: "commission",
        });
        batch.set(
          doc(db, "users", d.userId),
          {
            walletBalance: increment(d.amount),
            totalCommission: increment(d.amount),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        const refPayload = makeNotif({
          title: "Commission earned 🎉",
          body: `One of your referrals purchased “${
            row.courseTitle || "a course"
          }”. You earned ${fmt(row.currency, d.amount)} (Level ${d.level}).`,
          type: "commission",
          extra: {
            orderId: row.id,
            courseId: row.courseId,
            level: d.level,
            amount: d.amount,
          },
        });
        setAllNotifTargets(batch, d.userId, refPayload, {
          toUsers: MIRROR_REFERRER_NOTIF_TO_USERS,
          toReferralUsers: true,
        });
      }

      // instructor payout after affiliate/platform deductions
      if (instructorUid && instructorBase > 0) {
        const payoutRef = doc(collection(db, "payouts"));
        batch.set(payoutRef, {
          userId: instructorUid,
          orderId: row.id,
          courseId: row.courseId,
          courseTitle: row.courseTitle || "",
          amount: instructorBase,
          level: 0,
          role: "instructor",
          currency: row.currency || "LKR",
          status: "pending",
          createdAt: serverTimestamp(),
          source: "instructor_revenue",
        });
        batch.set(
          doc(db, "users", instructorUid),
          {
            walletBalance: increment(instructorBase),
            instructorEarnings: increment(instructorBase),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        const instPayload = makeNotif({
          title: "New instructor revenue 💼",
          body: `Your course “${row.courseTitle || "a course"}” sold. ${fmt(
            row.currency,
            instructorBase,
          )} has been credited to your wallet.`,
          type: "payout",
          extra: {
            orderId: row.id,
            courseId: row.courseId,
            amount: instructorBase,
          },
        });
        setAllNotifTargets(batch, instructorUid, instPayload);
      }

      // pools
      if (split.company > 0)
        batch.set(
          doc(db, "pools", "company"),
          { balance: increment(split.company), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (split.service > 0)
        batch.set(
          doc(db, "pools", "service"),
          { balance: increment(split.service), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (split.others > 0)
        batch.set(
          doc(db, "pools", "others"),
          { balance: increment(split.others), updatedAt: serverTimestamp() },
          { merge: true },
        );
      if (commissionPoolAmount > 0)
        batch.set(
          doc(db, "pools", "commission"),
          {
            balance: increment(commissionPoolAmount),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      // buyer notif
      const learnLink = `/learn/${row.courseId}`;
      const buyerPayload = makeNotif({
        title: "Payment approved ✅",
        body: `Your payment for “${
          row.courseTitle || "a course"
        }” is approved. Tap to start learning.`,
        type: "success",
        link: learnLink,
        cta: "Start learning",
        extra: { orderId: row.id, courseId: row.courseId, amount: row.amount },
      });
      setAllNotifTargets(batch, row.userId, buyerPayload);

      await batch.commit();

      // Optional email
      try {
        const buyerProfile = buyer || (await getUser(row.userId));
        const buyerEmail =
          row.buyerEmail ||
          row.userEmail ||
          row.email ||
          buyerProfile?.email ||
          buyerProfile?.contactEmail ||
          buyerProfile?.profile?.email ||
          null;

        if (buyerEmail) {
          const invoiceHtml = buildInvoiceHtml({ order: row, split, dist });
          const plainText =
            `Payment Approved\n\n` +
            `Order: ${row.id}\nCourse: ${row.courseTitle || "Course"}\n` +
            `Amount: ${row.currency || "LKR"} ${Number(
              row.amount || 0,
            ).toLocaleString()}\n\n` +
            `Start learning: ${window.location.origin}/learn/${row.courseId}\n`;

          await addDoc(collection(db, "mail"), {
            to: Array.isArray(buyerEmail) ? buyerEmail : [buyerEmail],
            from: "Ceylon Web Mall <no-reply@txtrademart.online>",
            replyTo: ["support@txtrademart.online"],
            message: {
              subject: `Invoice • Order ${row.id}`,
              text: plainText,
              html: invoiceHtml,
            },
            createdAt: serverTimestamp(),
            meta: {
              orderId: row.id,
              courseId: row.courseId,
              userId: row.userId,
            },
          });
        }
      } catch (e) {
        console.error("[mail] failed to queue email:", e);
      }

      setConfirmBusy(false);
      setModalOpen(false);
      alert("Approved, enrolled, commissions credited, and invoice queued.");
    } catch (err) {
      console.error("approve() failed:", err);
      setConfirmBusy(false);
      alert(`Approve failed: ${err.message}`);
    }
  };

  const decline = async (row, reason) => {
    setConfirmBusy(true);
    try {
      await updateDoc(doc(db, "orders", row.id), {
        status: "declined",
        declinedAt: serverTimestamp(),
        declineReason: reason || "Payment not verified",
      });

      // notify buyer with the reason
      const batch = writeBatch(db);
      const payload = makeNotif({
        title: "Payment declined",
        body: `Your payment for “${
          row.courseTitle || "a course"
        }” was declined. Reason: ${reason || "Payment not verified"}.`,
        type: "error",
        extra: { orderId: row.id, courseId: row.courseId },
      });
      setAllNotifTargets(batch, row.userId, payload);
      await batch.commit();
    } catch (e) {
      console.error("decline() failed:", e);
    }
    setConfirmBusy(false);
    setModalOpen(false);
    alert("Declined.");
  };

  /* --------------------------------- UI --------------------------------- */

  const instructorAmountToShow = splitPreview?.instructor || 0; // equals course.price
  const upliftToShow = upliftPreview?.upliftTotal || 0;

  // const renderBuyerChainCard = (d, keyPrefix = "buyer") => {
  //   const p = people[d.userId];
  //   return (
  //     <div
  //       key={`${keyPrefix}-${d.level}`}
  //       className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white"
  //     >
  //       <div className="text-xs text-gray-500">Level {d.level}</div>
  //       <div className="text-sm">
  //         <span className="text-gray-600">User</span>:{" "}
  //         <span className="font-medium">{p?.name || d.userId}</span>
  //       </div>
  //       {p?.phone ? (
  //         <div className="text-xs text-gray-500">{p.phone}</div>
  //       ) : null}
  //       <div className="text-base font-bold mt-1">
  //         {fmt(currentRow?.currency, d.amount)}
  //       </div>
  //     </div>
  //   );
  // };

  const renderBuyerChainCard = (d, keyPrefix = "buyer") => {
    const p = people[d.userId] || {};
    const name = p.name || d.userId;
    const phone = p.phone || p.whatsapp || "";
    const telHref = phone ? `tel:${(phone + "").replace(/[^\d+]/g, "")}` : null;

    return (
      <div
        key={`${keyPrefix}-${d.level}`}
        className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white"
      >
        <div className="text-xs text-gray-500">Level {d.level}</div>

        <div className="mt-0.5">
          <div className="text-sm font-medium">{name}</div>
          {phone ? (
            <a
              href={telHref}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
              title="Call"
            >
              {phone}
            </a>
          ) : (
            <div className="text-xs text-gray-400">No mobile on file</div>
          )}
        </div>

        <div className="text-base font-bold mt-2">
          {fmt(currentRow?.currency, d.amount)}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
      <h1 className="text-2xl font-bold mb-6">Payment Reviews</h1>
      <div className="bg-white rounded-2xl shadow divide-y">
        {rows.length === 0 && (
          <div className="p-6 text-gray-500">No submissions yet.</div>
        )}
        {rows.map((o) => (
          <div key={o.id} className="p-6 flex gap-6 items-start">
            <div className="flex-1">
              <div className="font-semibold">{o.courseTitle}</div>
              <div className="text-sm text-gray-600 mt-1">
                Order: {o.id} • Buyer: {people[o.userId]?.name || o.userId}
              </div>
              <div className="text-sm text-gray-600">
                Paid: {o.currency || "LKR"}{" "}
                {Number(o.amount || 0).toLocaleString()}
              </div>
              {(o.referrerUserId || o.referralCode) && (
                <div className="text-sm text-gray-600">
                  Referrer:{" "}
                  {people[o.referrerUserId]?.name || o.referrerUserId || "—"}{" "}
                  {o.referralCode ? `(code: ${o.referralCode})` : ""}
                </div>
              )}
              {o.slipUrl && (
                <div className="mt-2">
                  {o.slipType === "pdf" ? (
                    <a
                      className="text-blue-600 underline"
                      href={o.slipUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View PDF slip
                    </a>
                  ) : (
                    <a href={o.slipUrl} target="_blank" rel="noreferrer">
                      <img
                        src={o.slipUrl}
                        alt="slip"
                        className="w-56 rounded border"
                      />
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
                Course payment review is now handled in the instructor portal.
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─────────────── Modals ─────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div
            ref={modalRef}
            className="w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden"
            role="dialog"
            aria-modal="true"
          >
            {/* header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm opacity-90">
                    {modalKind === "approve"
                      ? "Approve Payment"
                      : "Decline Payment"}
                  </div>
                  <h2 className="text-xl font-bold leading-tight">
                    {currentRow?.courseTitle}
                  </h2>
                  <div className="text-xs opacity-90 mt-1">
                    Order {currentRow?.id}
                  </div>
                </div>
                <button
                  className="text-2xl leading-none -mt-2 hover:opacity-90"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {modalKind === "approve" ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white">
                      <div className="text-xs text-gray-500">Buyer</div>
                      <div className="font-medium">
                        {people[currentRow?.userId]?.name || currentRow?.userId}
                      </div>
                      {people[currentRow?.userId]?.phone ? (
                        <div className="text-xs text-gray-500">
                          {people[currentRow?.userId]?.phone}
                        </div>
                      ) : null}
                    </div>
                    <div className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white">
                      <div className="text-xs text-gray-500">Paid</div>
                      <div className="font-semibold">
                        {fmt(currentRow?.currency, currentRow?.amount)}
                      </div>
                    </div>
                  </div>

                  {/* Split Preview */}
                  <div className="rounded-2xl border p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">Split Preview</div>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        read-only
                      </span>
                    </div>
                    {splitPreview ? (
                      <div className="text-sm grid grid-cols-2 gap-y-2">
                        <div className="text-gray-600">End Price</div>
                        <div className="text-right font-medium">
                          {fmt(currentRow?.currency, splitPreview.endPrice)}
                        </div>

                        <div className="text-gray-600">Instructor (net)</div>
                        <div className="text-right font-semibold">
                          {fmt(currentRow?.currency, instructorAmountToShow)}
                          {upliftToShow > 0 ? (
                            <div className="text-xs text-gray-500 mt-0.5">
                              Uplift to referrals:{" "}
                              {fmt(currentRow?.currency, upliftToShow)}
                            </div>
                          ) : null}
                        </div>

                        <div className="text-gray-600">Company</div>
                        <div className="text-right font-medium">
                          {fmt(currentRow?.currency, splitPreview.company)}
                        </div>
                        <div className="text-gray-600">Service</div>
                        <div className="text-right font-medium">
                          {fmt(currentRow?.currency, splitPreview.service)}
                        </div>
                        <div className="text-gray-600">Others</div>
                        <div className="text-right font-medium">
                          {fmt(currentRow?.currency, splitPreview.others)}
                        </div>
                        <div className="text-gray-600">Commission (pool)</div>
                        <div className="text-right font-medium">
                          {fmt(currentRow?.currency, splitPreview.commission)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Calculating…</div>
                    )}
                  </div>

                  {/* Buyer‑side Commission Distribution */}
                  <div className="rounded-2xl border p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">
                        Buyer Referral Commission
                      </div>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        40% / 20% / 10% from commission
                      </span>
                    </div>

                    {buyerPreview?.dist?.length ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {buyerPreview.dist.map((d) => renderBuyerChainCard(d))}
                        <div className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white">
                          <div className="text-xs text-gray-500">Pool</div>
                          <div className="text-base font-bold">
                            {fmt(currentRow?.currency, buyerPreview.pool)}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-1">
                            Unassigned levels go to pool
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        No buyer referrer chain found.
                      </div>
                    )}
                  </div>

                  {/* Instructor Referral Uplift Preview */}
                  <div className="rounded-2xl border p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">
                        Instructor Referral Uplift
                      </div>
                      {upliftPreview.hasReferredInstructor ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                          {upliftPreview.upliftPctUsed}% of instructor • split
                          50/30/20
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-600">
                          Not applicable
                        </span>
                      )}
                    </div>

                    {!upliftPreview.hasReferredInstructor ||
                    upliftPreview.upliftTotal <= 0 ? (
                      <div className="text-sm text-gray-500">
                        No instructor‑referral uplift for this instructor.
                      </div>
                    ) : (
                      <>
                        <div className="text-sm grid grid-cols-2 gap-y-2 mb-3">
                          <div className="text-gray-600">Uplift total</div>
                          <div className="text-right font-semibold">
                            {fmt(
                              currentRow?.currency,
                              upliftPreview.upliftTotal,
                            )}
                          </div>
                          <div className="text-gray-600">Pool (unassigned)</div>
                          <div className="text-right font-medium">
                            {fmt(currentRow?.currency, upliftPreview.pool)}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {upliftPreview.dist.map((d) => {
                            const p = people[d.userId];
                            return (
                              <div
                                key={`uplift-${d.level}`}
                                className="rounded-xl border p-3 bg-gradient-to-br from-gray-50 to-white"
                              >
                                <div className="text-xs text-gray-500">
                                  Level {d.level}
                                </div>
                                <div className="text-sm">
                                  <span className="text-gray-600">User</span>:{" "}
                                  <span className="font-medium">
                                    {p?.name || d.userId || "—"}
                                  </span>
                                </div>
                                {p?.phone ? (
                                  <div className="text-xs text-gray-500">
                                    {p.phone}
                                  </div>
                                ) : null}
                                <div className="text-base font-bold mt-1">
                                  {fmt(currentRow?.currency, d.amount)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-semibold">
                    Reason for decline
                  </label>
                  <textarea
                    className="mt-2 w-full min-h-[110px] p-3 border rounded-xl bg-gray-50"
                    placeholder="e.g., Bank slip invalid, amount mismatch, duplicate payment…"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* footer */}
            <div className="px-6 pb-6 flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-xl border hover:bg-gray-50"
                onClick={() => setModalOpen(false)}
                disabled={confirmBusy}
              >
                Cancel
              </button>

              {modalKind === "approve" ? (
                <button
                  className={`px-4 py-2 rounded-xl text-white ${
                    confirmBusy
                      ? "bg-green-400"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                  onClick={() => approve(currentRow)}
                  disabled={confirmBusy}
                >
                  {confirmBusy ? "Approving…" : "Confirm Approve & Enroll"}
                </button>
              ) : (
                <button
                  className={`px-4 py-2 rounded-xl text-white ${
                    declineReason.trim()
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-red-300"
                  } `}
                  onClick={() => decline(currentRow, declineReason.trim())}
                  disabled={!declineReason.trim() || confirmBusy}
                >
                  {confirmBusy ? "Declining…" : "Confirm Decline"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
